export function hasActiveFilterDef(filterDef) {
  if (!filterDef) return false;
  if (filterDef.type === "daterange") return !!(filterDef.from || filterDef.to);
  if (filterDef.type === "text") return !!filterDef.value?.trim();
  return Array.isArray(filterDef.values) && filterDef.values.length > 0;
}

export function countActiveFilters(appliedFilters) {
  return Object.values(appliedFilters || {}).filter((filterDef) => hasActiveFilterDef(filterDef)).length;
}

export function buildFilterFields(tasks, users, filterFields, preloadedOptions = {}) {
  const safeTasks = Array.isArray(tasks) ? tasks : [];

  const uniq = (key) => [...new Set(safeTasks.map((t) => t[key]).filter(Boolean))].sort();

  return (filterFields || []).map((field) => {
    if (field.type === "daterange" || field.type === "text") return { ...field, options: [] };
    if (field.key === "tags") return { ...field, options: [] };

    // tmm_member from preloaded options is already [{value, label}]
    if (field.key === "tmm_member") {
      if (preloadedOptions["tmm_member"]) return { ...field, options: preloadedOptions["tmm_member"] };
      return { ...field, options: [] };
    }

    // staticOptions take priority; then preloaded; then fall back to current page scan
    const rawOptions = field.staticOptions
      || preloadedOptions[field.key]
      || uniq(field.key);

    // preloadedOptions for customer/project/feature/sub_feature return [{value: uuid, label: name}]
    // staticOptions and uniq fallback return plain strings → normalize to {value, label}
    const options = (rawOptions || []).map((v) =>
      (typeof v === "object" && v !== null) ? v : { value: v, label: v }
    );
    return { ...field, options };
  });
}

export function buildVisibleFilterFields(fields, search, appliedFilters = {}) {
  const q = (search || "").trim().toLowerCase();
  const filtered = !q
    ? [...(fields || [])]
    : (fields || []).filter((field) => field.label.toLowerCase().includes(q));

  return filtered.sort((a, b) => {
    const aActive = hasActiveFilterDef(appliedFilters?.[a.key]);
    const bActive = hasActiveFilterDef(appliedFilters?.[b.key]);
    if (aActive === bActive) return 0;
    return aActive ? -1 : 1;
  });
}

export function buildFilteredTasks({ tasks, appliedFilters, filterMode, search, sortKey, sortDir }) {
  let list = Array.isArray(tasks) ? tasks : [];

  const filterEntries = Object.entries(appliedFilters || {}).filter(([, filterDef]) => hasActiveFilterDef(filterDef));
  if (filterEntries.length > 0) {
    list = list.filter((task) => {
      const checks = filterEntries.map(([field, filterDef]) => {
        if (filterDef.type === "daterange") {
          const raw = task[field];
          if (!raw) return false;
          const datePart = raw.slice(0, 10);
          if (filterDef.from && datePart < filterDef.from) return false;
          if (filterDef.to && datePart > filterDef.to) return false;
          return true;
        }

        if (filterDef.type === "text") {
          return (task[field] || "").toLowerCase().includes((filterDef.value || "").toLowerCase());
        }

        if (field === "tags") {
          const taskTagNames = Array.isArray(task.tag_names)
            ? task.tag_names.map((n) => String(n).toLowerCase())
            : [];
          const match = filterDef.values.some((v) => taskTagNames.includes(String(v).toLowerCase()));
          return filterDef.op === "is_not" ? !match : match;
        }

        const taskValue = task[field] ?? "";
        const match = filterDef.values.includes(taskValue);
        return filterDef.op === "is_not" ? !match : match;
      });

      return filterMode === "all" ? checks.every(Boolean) : checks.some(Boolean);
    });
  }

  const searchText = (search || "").trim().toLowerCase();
  if (searchText) {
    list = list.filter((task) =>
      (task.task_code || "").toLowerCase().includes(searchText) ||
      (task.description || "").toLowerCase().includes(searchText) ||
      (task.customer || "").toLowerCase().includes(searchText) ||
      (task.project || "").toLowerCase().includes(searchText) ||
      (task.tmm_member || "").toLowerCase().includes(searchText) ||
      (task.feature || "").toLowerCase().includes(searchText) ||
      (task.visibility || "").toLowerCase().includes(searchText)
    );
  }

  return [...list].sort((a, b) => {
    const aValue = (a[sortKey] ?? "").toString().toLowerCase();
    const bValue = (b[sortKey] ?? "").toString().toLowerCase();
    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;
    return sortDir === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  });
}

export function buildCollapsedBoardCols(filteredTasks, boardCols) {
  const safeBoardCols = Array.isArray(boardCols) ? boardCols : [];
  const safeTasks = Array.isArray(filteredTasks) ? filteredTasks : [];
  return new Set(
    safeBoardCols.filter((col) => safeTasks.filter((task) => task.status === col).length === 0)
  );
}

export function areAllBoardColsCollapsed(collapsedCols, boardCols) {
  return (boardCols || []).every((col) => collapsedCols.has(col));
}

export function getCollapseAllSet(shouldCollapseAll, boardCols) {
  return shouldCollapseAll ? new Set(boardCols || []) : new Set();
}

export function buildProjectOptions(customers, selectedCustomer) {
  const entry = (customers || []).find((c) => c.customer_name === selectedCustomer);
  return entry?.projects || [];
}

export function buildSubFeatureOptions(features, featureName) {
  const entry = (features || []).find((f) => f.feature_name === featureName);
  return (entry?.sub_features || []).map((subFeature) => ({
    value: subFeature.sub_feature_name,
    label: subFeature.sub_feature_name,
  }));
}
